import { ChangeEvent, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { notify } from '../../toaster';
import { DATASET_UPLOAD_MUTATION } from '../hooks/useDatasetUpload';
import { Dataset, getColumnsFromMetadata } from '../types';
import { ImportProblems } from './ImportProblems';
import { UploadArea } from './UploadArea';
import { useFollowedUpload } from './useFollowedUpload';

// An empty dataset gets its first data uploaded; one with rows has them
// replaced.
export type ReplaceDataVariant = 'upload' | 'replace';

type ReplaceDataDialogProps = {
  // The dialog is open while a variant is set.
  variant: ReplaceDataVariant | null;
  onClose: () => void;
  table: Dataset;
};

// The form is unmounted while the dialog is closed, so a closed dialog drops
// its file and stops following its import; an import left running shows on
// the Data tab.
export const ReplaceDataDialog = ({
  variant,
  onClose,
  table,
}: ReplaceDataDialogProps) => {
  const sending = useIsMutating({ mutationKey: DATASET_UPLOAD_MUTATION }) > 0;
  return (
    <Dialog open={variant !== null} onClose={sending ? undefined : onClose}>
      {variant ? (
        <ReplaceDataForm variant={variant} onClose={onClose} table={table} />
      ) : null}
    </Dialog>
  );
};

const ReplaceDataForm = ({
  variant,
  onClose,
  table,
}: {
  variant: ReplaceDataVariant;
  onClose: () => void;
  table: Dataset;
}) => {
  const intl = useIntl();
  const [file, setFile] = useState<File | null>(null);
  const replacing = variant === 'replace';

  // The dialog waits for the import, so the problems it finds can show next
  // to the file.
  const { upload, sending, importing, failure, reset } = useFollowedUpload({
    onFinished: followed => {
      notify.default({
        title: intl.formatMessage({
          id: followed
            ? 'features.datasets.notification.importFinished'
            : 'features.datasets.notification.importStarted',
        }),
      });
      onClose();
    },
  });

  // Declared columns stay and the file is checked against them; inferred
  // ones are taken from the file again.
  const declared = table.schema_mode === 'validate';
  const descriptionId = replacing
    ? declared
      ? 'features.datasets.data.replace.description.declared'
      : 'features.datasets.data.replace.description.inferred'
    : declared
      ? 'features.datasets.data.upload.description.declared'
      : 'features.datasets.data.upload.description.inferred';

  const actionLabel = intl.formatMessage({
    id: replacing
      ? 'features.datasets.data.replace'
      : 'features.datasets.data.upload',
  });

  return (
    <Box
      component="form"
      // Keeps the title, content and actions laid out as the dialog's own.
      sx={{ display: 'contents' }}
      onSubmit={e => {
        e.preventDefault();
        if (file && !importing) {
          upload({
            file,
            datasetName: table.name,
            encoding: 'utf-8',
            dynamic: true,
          });
        }
      }}
    >
      {/* Closing before MDH accepts the file would hide why it didn't. */}
      <DialogTitle
        title={actionLabel}
        onClose={sending ? undefined : onClose}
      />
      <DialogContent>
        <Stack pt={2} spacing={2}>
          <Typography variant="body1">
            {intl.formatMessage({ id: descriptionId })}
          </Typography>
          {/* A file dropped mid-import would replace the one being imported. */}
          {importing ? null : (
            <UploadArea
              file={file}
              onUploadChange={(e: ChangeEvent<HTMLInputElement>) => {
                // A new file makes the problems found in the previous one
                // stale.
                reset();
                setFile(e.target.files?.[0] ?? null);
              }}
            />
          )}
          {failure ? (
            <ImportProblems
              failure={failure}
              columns={getColumnsFromMetadata(table.metadata, table.schema)}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          disabled={sending}
          onClick={onClose}
        >
          {intl.formatMessage({
            // Closing doesn't stop an import already running.
            id: importing
              ? 'features.datasets.data.close'
              : 'features.datasets.data.cancel',
          })}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!file || importing || failure?.kind === 'file'}
          endIcon={importing ? <CircularProgress size={14} /> : null}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Box>
  );
};
